import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useGrn, useGrnItems } from '../hooks/useGrn';
import { useZones } from '../hooks/useWarehouse';
import { warehouseService } from '../services/warehouseService';
import { grnService } from '../services/grnService';
import { gateEntryService } from '../services/gateEntryService';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../hooks/useConfirm';
import { api } from '../services/api';
import { Button, Badge } from '../components/common';
import MobileCardList, { TableColumn } from '../components/common/MobileCardList';
import { INPUT_SANITIZERS, FIELD_VALIDATORS } from '../utils/validationHelpers';

// ── Shared helpers/predicates (mirrors GrnCreate.tsx) ──────────────────────

// Integer-only quantity input: digits only (no sign, decimal, or exponent),
// leading zeros stripped. Mirrors GrnCreate.tsx.
const sanitizeIntegerQty = (value: string): string => {
  const digitsOnly = value.replace(/[^0-9]/g, '');
  if (!digitsOnly) return '';
  return digitsOnly.replace(/^0+(?=\d)/, '');
};

// Same idea as sanitizeIntegerQty but keeps a single decimal point, for
// sqft-style fields (pallet size). Mirrors GrnCreate.tsx.
const sanitizeDecimalQty = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, '');
  const firstDot = cleaned.indexOf('.');
  if (firstDot === -1) return cleaned;
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '');
};

// First-fit auto-assignment of one pallet per row across candidate zones'
// locations. Skips held/used locations and full ones; the last pallet may take a
// partially-free slot sized to the remainder. Mirrors GrnCreate.tsx.
function autoAssignFirstFit(
  containers: Array<{ id: number; locations: any[] }>,
  rowCount: number,
  qtyPerPallet: number | null | undefined,
  totalQty: number,
  alreadyUsedLocationIds: Set<number>,
): Array<{ containerId: number; locationId: number } | null> {
  const TOLERANCE = 0.5;
  const remainder = qtyPerPallet && qtyPerPallet > 0 ? totalQty % qtyPerPallet : 0;
  const used = new Set(alreadyUsedLocationIds);
  const results: Array<{ containerId: number; locationId: number } | null> = [];

  for (let i = 0; i < rowCount; i++) {
    const isLast = i === rowCount - 1;
    const requiredFreePct = isLast && remainder > 0 ? (remainder / (qtyPerPallet as number)) * 100 : 100;
    let picked: { containerId: number; locationId: number } | null = null;

    if (requiredFreePct < 100) {
      let bestPartial: { containerId: number; locationId: number; freePct: number } | null = null;
      for (const container of containers) {
        for (const loc of container.locations) {
          if (!loc?.locationId || used.has(loc.locationId) || loc.isHeld) continue;
          const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
          if (freePct >= 100 - TOLERANCE) continue; // fully empty — reserved for the fallback pass below
          if (freePct < requiredFreePct - TOLERANCE) continue;
          if (!bestPartial || freePct < bestPartial.freePct) {
            bestPartial = { containerId: container.id, locationId: loc.locationId, freePct };
          }
        }
      }
      if (bestPartial) picked = { containerId: bestPartial.containerId, locationId: bestPartial.locationId };
    }

    if (!picked) {
      outer: for (const container of containers) {
        for (const loc of container.locations) {
          if (!loc?.locationId || used.has(loc.locationId) || loc.isHeld) continue;
          const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
          if (freePct < requiredFreePct - TOLERANCE) continue;
          picked = { containerId: container.id, locationId: loc.locationId };
          break outer;
        }
      }
    }

    if (picked) used.add(picked.locationId);
    results.push(picked);
  }

  return results;
}

const isLbdZone = (zone: any): boolean => {
  const type = String(zone?.zoneType || '').toLowerCase();
  const code = String(zone?.code || '').toLowerCase();
  return type === 'lbd' || code.startsWith('lbd');
};

const isHeavyZone = (zone: any): boolean => {
  const type = String(zone?.zoneType || '').toLowerCase();
  const code = String(zone?.code || '').toLowerCase();
  return type === 'heavy' || code.startsWith('heavy');
};

const zoneTypeLabel = (zone: any): string => {
  const type = String(zone?.zoneType || '').toLowerCase();
  if (type === 'heavy') return 'Heavy';
  if (type === 'lbd') return 'LBD';
  return 'Regular';
};

const zoneOptionLabel = (zone: any): string => `${zone?.code ?? zone?.name ?? 'Unknown'} — ${zoneTypeLabel(zone)}`;

// An LBD rack holds damaged/c-damaged stock only and must never appear in the
// good-stock rack picker. Mirrors GrnCreate.tsx.
const isLbdRack = (rack: any): boolean => {
  const type = String(rack?.rackType || '').toLowerCase();
  const code = String(rack?.code || '').toLowerCase();
  return type === 'lbd' || code.startsWith('lbd');
};

const rackTypeLabel = (rack: any): string => {
  const type = String(rack?.rackType || '').toLowerCase();
  if (type === 'heavy') return 'Heavy';
  if (type === 'lbd') return 'LBD';
  return 'Regular';
};

const rackOptionLabel = (rack: any): string => `${rack?.code ?? rack?.name ?? 'Unknown'} — ${rackTypeLabel(rack)}`;

const formatLocationSpaceLabel = (loc: any): string => {
  if (!loc) return '';
  if (loc.status === 'occupied') return ' 🔒 (Occupied)';
  if (loc.isHeld) return ' 🔒 (Reserved by another item)';
  const availPct = loc.occupiedPercentage != null ? Math.max(0, Math.round(100 - Number(loc.occupiedPercentage))) : 100;
  const sqFtStr = loc.availableSqFt != null ? ` · ${loc.availableSqFt} sq ft avail.` : '';
  return ` — ${availPct}% free${sqFtStr}`;
};

const formatStorageLocations = (storageDetails: any): string => {
  if (!Array.isArray(storageDetails)) return '';
  return storageDetails
    .map((s: any) => (s && typeof s === 'object' ? s.zoneLocationCode || s.zoneCode || s.locationCode || s.rackCode || '' : ''))
    .filter(Boolean)
    .join(', ');
};

const toDateInputValue = (val: unknown): string => {
  if (!val) return '';
  const s = String(val);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

// LR Date can't be in the future — mirrors GrnCreate.tsx.
const todayStr = () => new Date().toISOString().slice(0, 10);

const getErrorMessage = (error: any, fallback: string) => {
  if (error?.errors && Array.isArray(error.errors)) return error.errors[0];
  return error?.message || fallback;
};

// ── Data model ──────────────────────────────────────────────────────────────
// Every line item lives only in local state until "Submit for Approval" bundles
// everything into one createPastGrnUpdate request — nothing here touches the
// live GRN, inventory, or storage until an Admin/Super Admin approves it.

type StagedAction = 'existing' | 'edited' | 'added';

interface StagedItem {
  _key: string;
  _action: StagedAction;
  // True only once the user has actually opened and saved the Pallet card for
  // this item in this session — storage is only ever replaced when this is
  // true; otherwise it's left exactly as it already was, valid or not.
  _storageTouched?: boolean;
  id?: number; // real grn_items.id — absent for items added on this page
  productCategoryId: number | null;
  productMasterId: number | null;
  productDescription: string;
  itemCode: string;
  unitType: string;
  weightPerItem: string;
  batchNo: string;
  skuCode: string;
  customerSapCode: string;
  mfgDate: string;
  expiryDate: string;
  goodQty: number;
  cdamQty: number;
  damQty: number;
  lotNo: string;
  handlingType: 'manual' | 'mhe' | '';
  remarks: string;
  totalWeight: number;
  qtyPerPallet: number | null;
  palletCount: number;
  requiredPalletCount: number | null;
  storageType: 'ground' | 'rack' | null;
  storageDetails: Array<{ zoneId?: number; pallets?: number; zoneLocationId?: number | null; rackId?: number; locationId?: number | null }>;
  isOversized: boolean;
  palletSizeSqft: number;
  lbdStorageMode: 'dedicated' | 'shared' | null;
  lbdPalletCount: number | null;
  lbdRemark: string | null;
  lbdSharedPalletRef: string | null;
  lbdStorageDetails: any[] | null;
}

const itemFromServer = (item: any, key: string): StagedItem => ({
  _key: key,
  _action: 'existing',
  id: item.id,
  productCategoryId: item.productCategoryId ?? null,
  productMasterId: item.productMasterId ?? null,
  productDescription: item.productDescription ?? '',
  itemCode: item.itemCode ?? '',
  unitType: item.unitType ?? '',
  weightPerItem: item.weightPerItem != null ? String(item.weightPerItem) : '',
  batchNo: item.batchNo ?? '',
  skuCode: item.skuCode ?? '',
  customerSapCode: item.customerSapCode ?? '',
  mfgDate: toDateInputValue(item.mfgDate),
  expiryDate: toDateInputValue(item.expiryDate),
  goodQty: item.goodQty ?? 0,
  cdamQty: item.cdamQty ?? 0,
  damQty: item.damQty ?? 0,
  lotNo: item.lotNo ?? '',
  handlingType: (item.handlingType as 'manual' | 'mhe' | '') ?? '',
  remarks: item.remarks ?? '',
  totalWeight: item.totalWeight != null ? Number(item.totalWeight) : 0,
  qtyPerPallet: item.qtyPerPallet ?? null,
  palletCount: item.palletCount ?? 0,
  requiredPalletCount: item.requiredPalletCount ?? null,
  storageType: item.storageType === 'rack' ? 'rack' : (item.storageType === 'ground' ? 'ground' : null),
  storageDetails: Array.isArray(item.storageDetails) ? item.storageDetails : [],
  isOversized: !!item.isOversized,
  palletSizeSqft: item.palletSizeSqft != null ? Number(item.palletSizeSqft) : 25,
  lbdStorageMode: item.lbdStorageMode ?? null,
  lbdPalletCount: item.lbdPalletCount ?? null,
  lbdRemark: item.lbdRemark ?? null,
  lbdSharedPalletRef: item.lbdSharedPalletRef ?? null,
  lbdStorageDetails: Array.isArray(item.lbdStorageDetails) ? item.lbdStorageDetails : null,
});

// The number of good-stock pallets to place. Prefers the stored requiredPalletCount,
// else derives it from good qty ÷ qty-per-pallet, else falls back to the saved
// pallet count (min 1). Used as the single source of truth for how many rows the
// pallet card generates and displays.
const requiredPalletsForItem = (item: StagedItem): number => {
  if (item.requiredPalletCount && item.requiredPalletCount > 0) return item.requiredPalletCount;
  if (item.qtyPerPallet && item.qtyPerPallet > 0 && item.goodQty > 0) return Math.ceil(item.goodQty / item.qtyPerPallet);
  return item.palletCount > 0 ? item.palletCount : 1;
};

interface ItemFormData {
  productCategoryId: number | null;
  productMasterId: number | null;
  productDescription: string;
  itemCode: string;
  unitType: string;
  weightPerItem: string;
  batchNo: string;
  skuCode: string;
  customerSapCode: string;
  mfgDate: string;
  expiryDate: string;
  goodQty: string;
  cdamQty: string;
  damQty: string;
  lotNo: string;
  handlingType: 'manual' | 'mhe' | '';
  remarks: string;
}

const emptyForm: ItemFormData = {
  productCategoryId: null,
  productMasterId: null,
  productDescription: '',
  itemCode: '',
  unitType: '',
  weightPerItem: '',
  batchNo: '',
  skuCode: '',
  customerSapCode: '',
  mfgDate: '',
  expiryDate: '',
  goodQty: '',
  cdamQty: '0',
  damQty: '0',
  lotNo: '',
  handlingType: '',
  remarks: '',
};

export default function GrnPastEditPage() {
  const { grnId: urlGrnId } = useParams<{ grnId: string }>();
  const grnId = urlGrnId ? parseInt(urlGrnId, 10) : null;
  const navigate = useNavigate();
  const toast = useToast();
  const { confirm, ConfirmDialog } = useConfirm();

  const { data: grnData, isLoading: isLoadingGrn } = useGrn(grnId);
  const { data: grnItemsData, isLoading: isLoadingItems } = useGrnItems(grnId);
  const grn = (grnData as any)?.data ?? grnData;
  const clientId: number | undefined = grn?.clientId;
  const warehouseId: number | undefined = grn?.warehouseId;

  const [stagedItems, setStagedItems] = useState<StagedItem[]>([]);
  const initializedRef = useRef(false);
  useEffect(() => {
    if (initializedRef.current) return;
    if (!Array.isArray(grnItemsData)) return;
    setStagedItems(grnItemsData.map((item: any) => itemFromServer(item, String(item.id))));
    initializedRef.current = true;
  }, [grnItemsData]);

  // ── Header fields — staged locally, same as GrnCreate's confirm-time bundle ──
  const [editReason, setEditReason] = useState('');
  const [clientLocation, setClientLocation] = useState('');
  const [grnRemarks, setGrnRemarks] = useState('');
  const [transporterName, setTransporterName] = useState('');
  const [lrNumber, setLrNumber] = useState('');
  const [lrNumberError, setLrNumberError] = useState<string | null>(null);
  const [lrDate, setLrDate] = useState('');
  const headerInitRef = useRef(false);
  useEffect(() => {
    if (headerInitRef.current || !grn) return;
    setClientLocation(grn.clientLocation || '');
    setGrnRemarks(grn.grnRemarks || '');
    setTransporterName(grn.transporterName || '');
    setLrNumber(grn.lrNumber || '');
    setLrDate(toDateInputValue(grn.lrDate));
    headerInitRef.current = true;
  }, [grn]);

  // ── Gate entry details — read-only, same source GrnCreate's "Gate Entry
  // Details" card reads (Invoice Number lives on the gate entry, not the GRN).
  const [gateEntry, setGateEntry] = useState<any>(null);
  useEffect(() => {
    if (!grn?.gateEntryId) return;
    let cancelled = false;
    gateEntryService.getGateEntryById(String(grn.gateEntryId))
      .then((res: any) => { if (!cancelled) setGateEntry(res?.data ?? res); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [grn?.gateEntryId]);

  // ── Product search (same product_master lookup GrnCreate uses) ──────────────
  const [productMasterList, setProductMasterList] = useState<any[]>([]);
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    api.get(`/master-data/products?clientId=${clientId}&page=1&limit=200`)
      .then((res: any) => { if (!cancelled) setProductMasterList(res?.data?.items ?? []); })
      .catch(() => { if (!cancelled) setProductMasterList([]); });
    return () => { cancelled = true; };
  }, [clientId]);

  const [batchList, setBatchList] = useState<any[]>([]);
  const loadBatchesForProduct = async (productMasterId: number) => {
    try {
      const res: any = await api.get(`/master-data/batches?productMasterId=${productMasterId}&page=1&limit=100`);
      setBatchList(res?.data?.items ?? []);
    } catch {
      setBatchList([]);
    }
  };

  // ── Item form ────────────────────────────────────────────────────────────
  const [formData, setFormData] = useState<ItemFormData>(emptyForm);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const itemFormRef = useRef<HTMLDivElement>(null);

  const actualQty = useMemo(() => {
    return (parseInt(formData.goodQty) || 0) + (parseInt(formData.cdamQty) || 0) + (parseInt(formData.damQty) || 0);
  }, [formData.goodQty, formData.cdamQty, formData.damQty]);

  // Rounded to 2dp — the backend rejects more (matches GrnCreate.tsx's .toFixed(2)),
  // and raw floating-point multiplication easily produces 3+ decimal places.
  const totalWeight = useMemo(
    () => Number((actualQty * (parseFloat(formData.weightPerItem) || 0)).toFixed(2)),
    [actualQty, formData.weightPerItem],
  );

  const selectedBatchQtyPerPallet = useMemo(() => {
    if (!formData.batchNo) return null;
    const batch = batchList.find((b: any) => b.batchNo === formData.batchNo);
    return batch?.qtyPerPallet ?? null;
  }, [formData.batchNo, batchList]);

  const goodQtyNumber = useMemo(() => parseInt(formData.goodQty) || 0, [formData.goodQty]);
  const computedRequiredPalletCount = useMemo(
    () => (selectedBatchQtyPerPallet && goodQtyNumber > 0 ? Math.ceil(goodQtyNumber / selectedBatchQtyPerPallet) : null),
    [selectedBatchQtyPerPallet, goodQtyNumber],
  );

  const selectProduct = (product: any) => {
    setFormData(f => ({
      ...f,
      productMasterId: product.id,
      productCategoryId: product.productCategoryId ?? f.productCategoryId,
      productDescription: product.productDescription,
      itemCode: product.itemCode ?? '',
      unitType: product.unitType ?? 'box',
      weightPerItem: product.weightPerUnit ?? '',
      batchNo: '',
      skuCode: '',
      customerSapCode: '',
      mfgDate: '',
      expiryDate: '',
    }));
    setProductSearch('');
    setShowProductDropdown(false);
    loadBatchesForProduct(product.id);
  };

  const populateFormFromItem = (item: StagedItem) => {
    setFormData({
      productCategoryId: item.productCategoryId,
      productMasterId: item.productMasterId,
      productDescription: item.productDescription,
      itemCode: item.itemCode,
      unitType: item.unitType,
      weightPerItem: item.weightPerItem,
      batchNo: item.batchNo,
      skuCode: item.skuCode,
      customerSapCode: item.customerSapCode,
      mfgDate: item.mfgDate,
      expiryDate: item.expiryDate,
      goodQty: String(item.goodQty),
      cdamQty: String(item.cdamQty),
      damQty: String(item.damQty),
      lotNo: item.lotNo,
      handlingType: item.handlingType,
      remarks: item.remarks,
    });
    if (item.productMasterId) loadBatchesForProduct(item.productMasterId);
  };

  const handleEditItem = (item: StagedItem) => {
    setEditingKey(item._key);
    populateFormFromItem(item);
    itemFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const validateForm = (): string | null => {
    if (!formData.productMasterId) return 'Product description is required';
    if (!formData.batchNo.trim()) return 'Batch number is required';
    if (!formData.unitType) return 'Unit type is required';
    if (!formData.handlingType) return 'Handling type is required';
    if (actualQty <= 0) return 'Total quantity must be greater than 0';
    if (formData.lotNo && formData.lotNo.trim().length > 20) return 'Lot No must not exceed 20 characters';
    if (formData.expiryDate && !formData.mfgDate) return 'Manufacturing date is required when expiry date is provided';
    if (formData.mfgDate && formData.expiryDate) {
      const mfg = new Date(formData.mfgDate);
      const exp = new Date(formData.expiryDate);
      if (isNaN(mfg.getTime()) || isNaN(exp.getTime())) return 'Invalid date format';
      if (exp <= mfg) return 'Expiry date must be greater than manufacturing date';
    }
    return null;
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingKey(null);
    setProductSearch('');
    setBatchList([]);
  };

  // Editing an existing line only — this page does not support adding new
  // items to an already-approved GRN.
  const handleUpdateItem = () => {
    if (!editingKey) return;
    const error = validateForm();
    if (error) { toast.error(error); return; }

    setStagedItems(prev => prev.map(it => {
      if (it._key !== editingKey) return it;
      return {
        ...it,
        _action: 'edited',
        productCategoryId: formData.productCategoryId,
        productMasterId: formData.productMasterId,
        productDescription: formData.productDescription,
        itemCode: formData.itemCode.trim(),
        unitType: formData.unitType,
        weightPerItem: formData.weightPerItem,
        batchNo: formData.batchNo.trim(),
        skuCode: formData.skuCode.trim(),
        customerSapCode: formData.customerSapCode.trim(),
        mfgDate: formData.mfgDate,
        expiryDate: formData.expiryDate,
        goodQty: parseInt(formData.goodQty) || 0,
        cdamQty: parseInt(formData.cdamQty) || 0,
        damQty: parseInt(formData.damQty) || 0,
        lotNo: formData.lotNo.trim(),
        handlingType: formData.handlingType,
        remarks: formData.remarks.trim(),
        totalWeight,
        qtyPerPallet: selectedBatchQtyPerPallet,
        requiredPalletCount: computedRequiredPalletCount,
      };
    }));
    toast.success('Item updated');
    resetForm();
  };

  // ── Zones + pallet assignment card (ground + LBD only — see note in review) ──
  const { data: zonesData } = useZones(warehouseId ?? null);
  const zones = useMemo(() => {
    const payload: any = (zonesData as any)?.data ?? zonesData;
    if (Array.isArray(payload?.items)) return payload.items;
    return [];
  }, [zonesData]);
  const heavyZones = useMemo(() => zones.filter(isHeavyZone), [zones]);
  const lbdZones = useMemo(() => zones.filter(isLbdZone), [zones]);
  // Non-oversized ground placement can go in either a Regular or a Heavy
  // zone — only LBD zones are off-limits for good stock. Oversized pallets
  // are still restricted to Heavy zones only (enforced where cardIsOversized
  // is checked below and in handleSavePalletAssignment).
  const nonLbdZones = useMemo(() => zones.filter((z: any) => !isLbdZone(z)), [zones]);

  const [zoneLocationsByZoneId, setZoneLocationsByZoneId] = useState<Record<number, any[]>>({});
  const [loadingZoneLocationsByZoneId, setLoadingZoneLocationsByZoneId] = useState<Record<number, boolean>>({});
  const ensureZoneLocationsLoaded = useCallback(async (zoneId: number) => {
    if (zoneLocationsByZoneId[zoneId] || loadingZoneLocationsByZoneId[zoneId]) return;
    setLoadingZoneLocationsByZoneId(prev => ({ ...prev, [zoneId]: true }));
    try {
      const response: any = await warehouseService.getZoneLocationsWithOccupancy(zoneId);
      setZoneLocationsByZoneId(prev => ({ ...prev, [zoneId]: response?.locations || [] }));
    } catch {
      setZoneLocationsByZoneId(prev => ({ ...prev, [zoneId]: [] }));
    } finally {
      setLoadingZoneLocationsByZoneId(prev => ({ ...prev, [zoneId]: false }));
    }
  }, [zoneLocationsByZoneId, loadingZoneLocationsByZoneId]);

  // ── Racks (good-stock only; LBD stays zone-based) ──
  const [racks, setRacks] = useState<any[]>([]);
  useEffect(() => {
    if (!warehouseId) { setRacks([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const response: any = await warehouseService.getRacks(warehouseId, { limit: 100, status: 'active' });
        if (!cancelled) setRacks(response?.items || []);
      } catch {
        if (!cancelled) setRacks([]);
      }
    })();
    return () => { cancelled = true; };
  }, [warehouseId]);
  const racksSorted = useMemo(
    () => [...racks].filter((r: any) => !isLbdRack(r)).sort((a: any, b: any) => String(a?.code ?? '').localeCompare(String(b?.code ?? ''))),
    [racks],
  );

  const [locationsByRackId, setLocationsByRackId] = useState<Record<number, any[]>>({});
  const [loadingLocationsByRackId, setLoadingLocationsByRackId] = useState<Record<number, boolean>>({});
  const ensureRackLocationsLoaded = useCallback(async (rackId: number) => {
    if (locationsByRackId[rackId] || loadingLocationsByRackId[rackId]) return;
    setLoadingLocationsByRackId(prev => ({ ...prev, [rackId]: true }));
    try {
      const response: any = await warehouseService.getRackLocationsWithOccupancy(rackId);
      setLocationsByRackId(prev => ({ ...prev, [rackId]: response?.locations || [] }));
    } catch {
      setLocationsByRackId(prev => ({ ...prev, [rackId]: [] }));
    } finally {
      setLoadingLocationsByRackId(prev => ({ ...prev, [rackId]: false }));
    }
  }, [locationsByRackId, loadingLocationsByRackId]);

  const [assigningKey, setAssigningKey] = useState<string | null>(null);
  const [cardStorageType, setCardStorageType] = useState<'ground' | 'rack'>('ground');
  const [cardGroundPlacements, setCardGroundPlacements] = useState<Array<{ zoneId: number | ''; pallets: string; zoneLocationId?: number | null }>>([]);
  const [cardRackPlacements, setCardRackPlacements] = useState<Array<{ rackId: number | null; locationId: number | null }>>([]);
  const [cardIsOversized, setCardIsOversized] = useState(false);
  const [cardPalletSizeSqft, setCardPalletSizeSqft] = useState('');
  const [cardLbdPlacementType, setCardLbdPlacementType] = useState<'zone' | 'rack'>('zone');
  const [cardLbdPlacements, setCardLbdPlacements] = useState<Array<{ zoneId: number | ''; pallets: string; zoneLocationId?: number | null; rackId?: number | null; locationId?: number | null }>>([]);
  const [cardLbdRemark, setCardLbdRemark] = useState('');
  const lbdRacksSorted = useMemo(
    () => [...racks].filter((r: any) => isLbdRack(r)).sort((a: any, b: any) => String(a?.code ?? '').localeCompare(String(b?.code ?? ''))),
    [racks],
  );

  const currentAssigningItem = useMemo(
    () => stagedItems.find(it => it._key === assigningKey) || null,
    [stagedItems, assigningKey],
  );

  // Auto-populate empty Ground rows with the first available zone-location
  // (first-fit), once every candidate zone's locations have loaded. Rows the user
  // already touched are left alone. Mirrors GrnCreate.tsx.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardStorageType !== 'ground') return;
    if (cardGroundPlacements.length === 0) return;
    if (!cardGroundPlacements.some(p => p.zoneId === '')) return;

    const zonesForAssign = cardIsOversized ? heavyZones : nonLbdZones;
    if (zonesForAssign.length === 0) return;

    const unloaded = zonesForAssign.filter((z: any) => !zoneLocationsByZoneId[z.id] && !loadingZoneLocationsByZoneId[z.id]);
    if (unloaded.length > 0) { unloaded.forEach((z: any) => void ensureZoneLocationsLoaded(z.id)); return; }
    if (zonesForAssign.some((z: any) => loadingZoneLocationsByZoneId[z.id])) return;

    const goodQty = currentAssigningItem.goodQty || 0;
    const qtyPerPallet = currentAssigningItem.qtyPerPallet;
    const alreadyUsed = new Set(cardGroundPlacements.filter(p => p.zoneLocationId).map(p => p.zoneLocationId as number));
    const containers = zonesForAssign.map((z: any) => ({ id: z.id, locations: zoneLocationsByZoneId[z.id] || [] }));
    const picks = autoAssignFirstFit(containers, cardGroundPlacements.length, qtyPerPallet, goodQty, alreadyUsed);

    let changed = false;
    const next = cardGroundPlacements.map((row, idx) => {
      if (row.zoneId !== '') return row;
      const pick = picks[idx];
      if (!pick) return row;
      changed = true;
      return { ...row, zoneId: pick.containerId, zoneLocationId: pick.locationId, pallets: '1' };
    });
    if (changed) setCardGroundPlacements(next);
  }, [currentAssigningItem, cardStorageType, cardGroundPlacements, cardIsOversized, nonLbdZones, heavyZones, zoneLocationsByZoneId, loadingZoneLocationsByZoneId, ensureZoneLocationsLoaded]);

  // Auto-populate empty Rack rows the same first-fit way, across good-stock racks.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardStorageType !== 'rack') return;
    if (cardRackPlacements.length === 0) return;
    if (!cardRackPlacements.some(p => p.rackId === null)) return;
    if (racksSorted.length === 0) return;

    const unloaded = racksSorted.filter((r: any) => !locationsByRackId[r.id] && !loadingLocationsByRackId[r.id]);
    if (unloaded.length > 0) { unloaded.forEach((r: any) => void ensureRackLocationsLoaded(r.id)); return; }
    if (racksSorted.some((r: any) => loadingLocationsByRackId[r.id])) return;

    const goodQty = currentAssigningItem.goodQty || 0;
    const qtyPerPallet = currentAssigningItem.qtyPerPallet;
    const alreadyUsed = new Set(cardRackPlacements.filter(p => p.locationId).map(p => p.locationId as number));
    const containers = racksSorted.map((r: any) => ({ id: r.id, locations: locationsByRackId[r.id] || [] }));
    const picks = autoAssignFirstFit(containers, cardRackPlacements.length, qtyPerPallet, goodQty, alreadyUsed);

    let changed = false;
    const next = cardRackPlacements.map((row, idx) => {
      if (row.rackId !== null) return row;
      const pick = picks[idx];
      if (!pick) return row;
      changed = true;
      return { rackId: pick.containerId, locationId: pick.locationId };
    });
    if (changed) setCardRackPlacements(next);
  }, [currentAssigningItem, cardStorageType, cardRackPlacements, racksSorted, locationsByRackId, loadingLocationsByRackId, ensureRackLocationsLoaded]);

  // Keep the number of Ground rows exactly equal to Required Pallets while the card
  // is open — grow with empty rows (the auto-assign effect fills them), trim extras.
  // Guarantees the generated count always reflects Required Pallets.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardStorageType !== 'ground') return;
    const required = requiredPalletsForItem(currentAssigningItem);
    if (required <= 0 || cardGroundPlacements.length === required) return;
    setCardGroundPlacements(prev => {
      if (prev.length === required) return prev;
      if (prev.length > required) return prev.slice(0, required);
      return [...prev, ...Array.from({ length: required - prev.length }, () => ({ zoneId: '' as number | '', pallets: '1', zoneLocationId: null }))];
    });
  }, [currentAssigningItem, cardStorageType, cardGroundPlacements.length]);

  // Same guarantee for Rack rows.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardStorageType !== 'rack') return;
    const required = requiredPalletsForItem(currentAssigningItem);
    if (required <= 0 || cardRackPlacements.length === required) return;
    setCardRackPlacements(prev => {
      if (prev.length === required) return prev;
      if (prev.length > required) return prev.slice(0, required);
      return [...prev, ...Array.from({ length: required - prev.length }, () => ({ rackId: null, locationId: null }))];
    });
  }, [currentAssigningItem, cardStorageType, cardRackPlacements.length]);

  // Auto-populate empty LBD rows the same first-fit way, across LBD zones.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardLbdPlacementType !== 'zone') return;
    if (cardLbdPlacements.length === 0) return;
    if (!cardLbdPlacements.some(p => p.zoneId === '')) return;
    if (lbdZones.length === 0) return;

    const unloaded = lbdZones.filter((z: any) => !zoneLocationsByZoneId[z.id] && !loadingZoneLocationsByZoneId[z.id]);
    if (unloaded.length > 0) { unloaded.forEach((z: any) => void ensureZoneLocationsLoaded(z.id)); return; }
    if (lbdZones.some((z: any) => loadingZoneLocationsByZoneId[z.id])) return;

    const damagedQty = (currentAssigningItem.cdamQty || 0) + (currentAssigningItem.damQty || 0);
    const qtyPerPallet = currentAssigningItem.qtyPerPallet;
    const alreadyUsed = new Set(cardLbdPlacements.filter(p => p.zoneLocationId).map(p => p.zoneLocationId as number));
    const containers = lbdZones.map((z: any) => ({ id: z.id, locations: zoneLocationsByZoneId[z.id] || [] }));
    const picks = autoAssignFirstFit(containers, cardLbdPlacements.length, qtyPerPallet, damagedQty, alreadyUsed);

    let changed = false;
    const next = cardLbdPlacements.map((row, idx) => {
      if (row.zoneId !== '') return row;
      const pick = picks[idx];
      if (!pick) return row;
      changed = true;
      return { ...row, zoneId: pick.containerId, zoneLocationId: pick.locationId, pallets: '1' };
    });
    if (changed) setCardLbdPlacements(next);
  }, [currentAssigningItem, cardLbdPlacementType, cardLbdPlacements, lbdZones, zoneLocationsByZoneId, loadingZoneLocationsByZoneId, ensureZoneLocationsLoaded]);

  // Auto-populate empty LBD rows the same first-fit way, across LBD racks —
  // mirrors the zone version above, and the good-stock rack auto-assign effect.
  useEffect(() => {
    if (!currentAssigningItem) return;
    if (cardLbdPlacementType !== 'rack') return;
    if (cardLbdPlacements.length === 0) return;
    if (!cardLbdPlacements.some(p => !p.rackId)) return;
    if (lbdRacksSorted.length === 0) return;

    const unloaded = lbdRacksSorted.filter((r: any) => !locationsByRackId[r.id] && !loadingLocationsByRackId[r.id]);
    if (unloaded.length > 0) { unloaded.forEach((r: any) => void ensureRackLocationsLoaded(r.id)); return; }
    if (lbdRacksSorted.some((r: any) => loadingLocationsByRackId[r.id])) return;

    const damagedQty = (currentAssigningItem.cdamQty || 0) + (currentAssigningItem.damQty || 0);
    const qtyPerPallet = currentAssigningItem.qtyPerPallet;
    const alreadyUsed = new Set(cardLbdPlacements.filter(p => p.locationId).map(p => p.locationId as number));
    const containers = lbdRacksSorted.map((r: any) => ({ id: r.id, locations: locationsByRackId[r.id] || [] }));
    const picks = autoAssignFirstFit(containers, cardLbdPlacements.length, qtyPerPallet, damagedQty, alreadyUsed);

    let changed = false;
    const next = cardLbdPlacements.map((row, idx) => {
      if (row.rackId) return row;
      const pick = picks[idx];
      if (!pick) return row;
      changed = true;
      return { ...row, rackId: pick.containerId, locationId: pick.locationId, pallets: '1' };
    });
    if (changed) setCardLbdPlacements(next);
  }, [currentAssigningItem, cardLbdPlacementType, cardLbdPlacements, lbdRacksSorted, locationsByRackId, loadingLocationsByRackId, ensureRackLocationsLoaded]);

  const resetCardAssignment = () => {
    setAssigningKey(null);
    setCardStorageType('ground');
    setCardGroundPlacements([]);
    setCardRackPlacements([]);
    setCardIsOversized(false);
    setCardPalletSizeSqft('');
    setCardLbdPlacementType('zone');
    setCardLbdPlacements([]);
    setCardLbdRemark('');
  };

  const openPalletCard = (item: StagedItem) => {
    setAssigningKey(item._key);
    setCardIsOversized(item.isOversized);
    setCardPalletSizeSqft(item.isOversized ? String(item.palletSizeSqft) : '');
    // One pallet per row. The item's existing storage type stays selected; the
    // active type reuses its existing placements, and both types are seeded to the
    // required pallet count so switching the toggle always shows the right rows.
    const storageType: 'ground' | 'rack' = item.storageType === 'rack' ? 'rack' : 'ground';
    setCardStorageType(storageType);
    const details = item.storageDetails || [];
    // Always generate exactly Required-Pallets rows (one pallet each). Existing
    // placements are reused for as many rows as fit; any extra rows are left empty
    // for the first-fit auto-assign to populate.
    const seedCount = requiredPalletsForItem(item);
    const existingGround = storageType === 'ground' ? details : [];
    setCardGroundPlacements(
      Array.from({ length: seedCount }, (_, i) => {
        const s: any = existingGround[i];
        return s
          ? { zoneId: (s.zoneId ?? '') as number | '', pallets: String(s.pallets ?? 1), zoneLocationId: s.zoneLocationId ?? null }
          : { zoneId: '', pallets: '1', zoneLocationId: null };
      }),
    );
    const existingRack = storageType === 'rack' ? details : [];
    setCardRackPlacements(
      Array.from({ length: seedCount }, (_, i) => {
        const s: any = existingRack[i];
        return s
          ? { rackId: s.rackId ?? null, locationId: s.locationId ?? null }
          : { rackId: null, locationId: null };
      }),
    );
    setCardLbdRemark(item.lbdRemark || '');
    // LBD is dedicated-only, one pallet per location, and can be either
    // zone-based or rack-based (same either/or the main storage type offers).
    // Reuse existing dedicated rows, else seed the estimated LBD pallet count.
    // Legacy shared-mode items reseed.
    const lbdDetails = item.lbdStorageMode === 'shared' ? [] : (item.lbdStorageDetails || []);
    const lbdIsRackBased = lbdDetails.length > 0 && lbdDetails.every((d: any) => d.rackId != null);
    setCardLbdPlacementType(lbdIsRackBased ? 'rack' : 'zone');
    const damagedForSeed = item.cdamQty + item.damQty;
    const seedLbdCount = item.qtyPerPallet && item.qtyPerPallet > 0 ? Math.ceil(damagedForSeed / item.qtyPerPallet) : (damagedForSeed > 0 ? 1 : 0);
    setCardLbdPlacements(
      Array.from({ length: seedLbdCount }, (_, i) => {
        const d: any = lbdDetails[i];
        if (!d) return { zoneId: '', pallets: '1', zoneLocationId: null, rackId: null, locationId: null };
        return lbdIsRackBased
          ? { zoneId: '', pallets: '1', zoneLocationId: null, rackId: d.rackId ?? null, locationId: d.locationId ?? null }
          : { zoneId: (d.zoneId ?? '') as number | '', pallets: String(d.pallets ?? 1), zoneLocationId: d.zoneLocationId ?? null };
      }),
    );
    // Pre-load locations already referenced so occupancy shows correctly.
    for (const s of details) {
      if (storageType === 'rack') { if (s.rackId) void ensureRackLocationsLoaded(s.rackId); }
      else if (s.zoneId) void ensureZoneLocationsLoaded(s.zoneId);
    }
    for (const d of (item.lbdStorageDetails as any[]) || []) {
      if (d?.rackId) void ensureRackLocationsLoaded(d.rackId);
      else if (d?.zoneId) void ensureZoneLocationsLoaded(d.zoneId);
    }
  };

  const handleSavePalletAssignment = (item: StagedItem) => {
    const isRack = cardStorageType === 'rack';

    // Storage details come from the active placement type (one pallet per row).
    const storageDetails: StagedItem['storageDetails'] = isRack
      ? cardRackPlacements
          .filter(p => p.rackId && p.locationId)
          .map(p => ({ rackId: p.rackId as number, locationId: p.locationId as number }))
      : cardGroundPlacements
          .filter(p => p.zoneId !== '' && p.zoneLocationId)
          .map(p => ({ zoneId: p.zoneId as number, pallets: parseInt(p.pallets) || 0, zoneLocationId: p.zoneLocationId ?? undefined }));

    if (storageDetails.length === 0) { toast.error('Assign a storage location'); return; }
    if (isRack) {
      if (cardRackPlacements.some(p => !p.rackId || !p.locationId)) { toast.error('Select a rack and a specific location for every row'); return; }
      const rackLocationIds = storageDetails.map(p => p.locationId);
      if (new Set(rackLocationIds).size !== rackLocationIds.length) { toast.error('Storage location is already assigned. Please select a different location.'); return; }
    } else {
      if (cardGroundPlacements.some(p => p.zoneId !== '' && !p.zoneLocationId)) { toast.error('Select a specific location for every zone row'); return; }
      const groundLocationIds = storageDetails.map(p => p.zoneLocationId);
      if (new Set(groundLocationIds).size !== groundLocationIds.length) { toast.error('Storage location is already assigned. Please select a different location.'); return; }
    }

    // Pallet count is system-driven off Required Pallets — no manual entry field.
    const totalPallets = isRack ? storageDetails.length : storageDetails.reduce((s, p) => s + (p.pallets || 0), 0);
    const requiredPallets = requiredPalletsForItem(item);
    const palletCount = requiredPallets;
    if (totalPallets !== requiredPallets) {
      const diff = requiredPallets - totalPallets;
      toast.error(diff > 0
        ? `You require ${requiredPallets} pallets, ${diff} more needed`
        : `You require ${requiredPallets} pallets, remove ${Math.abs(diff)}`);
      return;
    }

    // Oversized (heavy-zone) applies to ground placement only.
    const oversized = !isRack && cardIsOversized;
    const palletSizeSqft = oversized ? parseFloat(cardPalletSizeSqft) : 25;
    if (oversized) {
      if (!cardPalletSizeSqft || !(palletSizeSqft > 0)) { toast.error('Enter the oversized pallet size in sqft'); return; }
      const allHeavy = storageDetails.every(s => heavyZones.some((z: any) => z.id === s.zoneId));
      if (!allHeavy) { toast.error('Oversized pallets can only be stored in a Heavy zone'); return; }
    }

    const damagedQty = item.cdamQty + item.damQty;
    const isLbdRackMode = cardLbdPlacementType === 'rack';
    let lbdFields: Pick<StagedItem, 'lbdStorageMode' | 'lbdPalletCount' | 'lbdRemark' | 'lbdSharedPalletRef' | 'lbdStorageDetails'>;
    if (damagedQty > 0) {
      if (cardLbdPlacements.length === 0) { toast.error('Add at least one LBD location for the damaged stock'); return; }
      if (isLbdRackMode) {
        if (cardLbdPlacements.some(p => !p.rackId || !p.locationId)) { toast.error('Select a rack and a specific location for every LBD pallet row'); return; }
        const lbdLocationIds = cardLbdPlacements.map(p => p.locationId);
        if (new Set(lbdLocationIds).size !== lbdLocationIds.length) { toast.error('This LBD location is already assigned. Please select another location.'); return; }
      } else {
        if (cardLbdPlacements.some(p => p.zoneId === '')) { toast.error('Select an LBD zone for every damaged-stock pallet row'); return; }
        if (cardLbdPlacements.some(p => !p.zoneLocationId)) { toast.error('LBD Location is required for every damaged-stock pallet row'); return; }
        if (cardLbdPlacements.some(p => (parseInt(p.pallets) || 0) !== 1)) { toast.error('Only one pallet can be assigned to each LBD location. Add a separate location for additional pallets.'); return; }
        const lbdLocationIds = cardLbdPlacements.map(p => p.zoneLocationId);
        if (new Set(lbdLocationIds).size !== lbdLocationIds.length) { toast.error('This LBD location is already assigned. Please select another location.'); return; }
      }
      lbdFields = {
        lbdStorageMode: 'dedicated', lbdPalletCount: cardLbdPlacements.length, lbdRemark: cardLbdRemark.trim() || null,
        lbdSharedPalletRef: null,
        lbdStorageDetails: isLbdRackMode
          ? cardLbdPlacements.map(p => ({ rackId: p.rackId as number, locationId: p.locationId as number }))
          : cardLbdPlacements.map(p => ({ zoneId: p.zoneId, pallets: parseInt(p.pallets) || 0, zoneLocationId: p.zoneLocationId ?? undefined })),
      };
    } else {
      lbdFields = { lbdStorageMode: null, lbdPalletCount: null, lbdRemark: null, lbdSharedPalletRef: null, lbdStorageDetails: null };
    }

    setStagedItems(prev => prev.map(it => {
      if (it._key !== item._key) return it;
      return {
        ...it,
        _action: 'edited',
        _storageTouched: true,
        palletCount, requiredPalletCount: requiredPallets, storageType: isRack ? 'rack' : 'ground', storageDetails,
        isOversized: oversized, palletSizeSqft, ...lbdFields,
      };
    }));
    resetCardAssignment();
    toast.success('Pallet assignment staged');
  };

  // ── Submit ───────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!grnId) return;
    if (!editReason.trim()) { toast.error('Please provide a reason for this edit'); return; }
    if (stagedItems.length === 0) { toast.error('At least one item is required'); return; }
    const lrError = FIELD_VALIDATORS.lrNumber(lrNumber);
    if (lrError) {
      setLrNumberError(lrError);
      toast.error(lrError);
      return;
    }
    if (lrDate && lrDate > todayStr()) {
      toast.error('LR Date cannot be in the future');
      return;
    }
    // Any item touched this session (quantity/product edit or a fresh pallet
    // assignment) must go through the Pallet card before submit — editing an
    // item's quantities can invalidate its previous pallet count/placement,
    // so a stale, untouched assignment is never good enough once edited.
    // Items nobody touched at all keep whatever storage they already had.
    for (const item of stagedItems) {
      if (item._action === 'existing') continue;
      if (!item._storageTouched) {
        toast.error(`Please assign pallets for "${item.productDescription}" using the Pallet button`);
        return;
      }
      if (!item.palletCount || item.storageDetails.length === 0) {
        toast.error(`Please assign pallets for "${item.productDescription}"`);
        return;
      }
      if (item.cdamQty + item.damQty > 0 && !item.lbdStorageMode) {
        toast.error(`Assign an LBD zone for the damaged stock on "${item.productDescription}"`);
        return;
      }
    }

    const ok = await confirm({
      title: 'Submit for approval?',
      message: 'This request will be sent to an Admin/Super Admin for review. Nothing changes until it is approved.',
      confirmText: 'Submit',
    });
    if (!ok) return;

    setIsSubmitting(true);
    try {
      const items = stagedItems
        .filter(it => it._action !== 'existing')
        .map(it => {
          // Storage/LBD placement is only sent — and therefore only ever
          // reassigned on approval — for items the user actually replaced via
          // the Pallet card this session. Otherwise the existing placement
          // (whatever it is, rack or ground) is left completely untouched.
          const storageFields = it._storageTouched
            ? {
                storageType: it.storageType ?? undefined,
                storageDetails: it.storageDetails,
                isOversized: it.isOversized,
                palletSizeSqft: it.palletSizeSqft,
                lbdStorageMode: it.lbdStorageMode ?? undefined,
                lbdPalletCount: it.lbdPalletCount ?? undefined,
                lbdRemark: it.lbdRemark ?? undefined,
                lbdSharedPalletRef: it.lbdSharedPalletRef ?? undefined,
                lbdStorageDetails: it.lbdStorageDetails ?? undefined,
              }
            : {};
          const base = {
            productDescription: it.productDescription,
            productCategoryId: it.productCategoryId ?? undefined,
            productMasterId: it.productMasterId ?? undefined,
            itemCode: it.itemCode || undefined,
            unitType: it.unitType,
            weightPerItem: it.weightPerItem ? parseFloat(it.weightPerItem) : undefined,
            goodQty: it.goodQty,
            cdamQty: it.cdamQty,
            damQty: it.damQty,
            palletCount: it.palletCount,
            requiredPalletCount: it.requiredPalletCount ?? undefined,
            qtyPerPallet: it.qtyPerPallet ?? undefined,
            ...storageFields,
            totalWeight: it.totalWeight,
            batchNo: it.batchNo || undefined,
            skuCode: it.skuCode || undefined,
            customerSapCode: it.customerSapCode || undefined,
            mfgDate: it.mfgDate || undefined,
            expiryDate: it.expiryDate || undefined,
            lotNo: it.lotNo || undefined,
            handlingType: it.handlingType || undefined,
            remarks: it.remarks || undefined,
          };
          return it._action === 'added'
            ? { action: 'add' as const, ...base }
            : { action: 'edit' as const, grnItemId: it.id, ...base };
        });

      await grnService.createPastGrnUpdate(grnId, {
        editReason: editReason.trim(),
        header: {
          clientLocation: clientLocation.trim() || undefined,
          grnRemarks: grnRemarks.trim() || undefined,
          transporterName: transporterName.trim() || undefined,
          lrNumber: lrNumber.trim() || undefined,
          lrDate: lrDate || undefined,
        } as any,
        items: items as any,
      });

      toast.success('Edit request submitted for admin approval');
      navigate('/inward');
    } catch (error: any) {
      toast.error(getErrorMessage(error, 'Failed to submit edit request'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingGrn || isLoadingItems) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
          <div className="text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600 mx-auto mb-3"></div>
            <p className="text-sm text-gray-600">Loading GRN…</p>
          </div>
        </div>
      </Layout>
    );
  }

  const filteredProducts = productMasterList
    .filter(p => !productSearch || p.productDescription?.toLowerCase().includes(productSearch.toLowerCase()))
    .slice(0, 50);

  // Shared column definitions drive both the mobile card list and desktop table.
  const itemColumns: TableColumn[] = [
    { key: 'productDescription', label: 'Product' },
    { key: 'batchNo', label: 'Batch', render: v => v || '-' },
    { key: 'goodQty', label: 'Good', align: 'right' },
    { key: 'cdamQty', label: 'C.DAM', align: 'right' },
    { key: 'damQty', label: 'DAM', align: 'right' },
    { key: 'palletCount', label: 'Pallets', align: 'right', render: v => v || 0 },
    {
      key: 'storage',
      label: 'Storage',
      render: (_v, row) => formatStorageLocations(row.storageDetails)
        || <span className="text-amber-600 font-medium">Not assigned</span>,
    },
    {
      key: 'status',
      label: 'Status',
      render: (_v, row) => row._action === 'existing'
        ? <span className="text-gray-400">—</span>
        : <Badge variant={row._action === 'added' ? 'success' : 'warning'} size="sm" rounded>{row._action === 'added' ? 'New' : 'Edited'}</Badge>,
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (_v, row) => (
        <div className="flex items-center gap-2 justify-end">
          <button
            onClick={() => handleEditItem(row)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 transition-colors"
          >
            <i className="fas fa-pen text-[10px]"></i> Edit
          </button>
          <button
            onClick={() => (assigningKey === row._key ? resetCardAssignment() : openPalletCard(row))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 transition-colors"
          >
            <i className="fas fa-pallet text-[10px]"></i> {assigningKey === row._key ? 'Close' : 'Pallet'}
          </button>
        </div>
      ),
    },
  ];

  return (
    <Layout>
      <div className="min-h-screen px-1 py-6 lg:p-6 bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Header */}
        <div className="mb-6 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex flex-wrap items-center justify-between gap-3 bg-gradient-to-r from-brand-100 to-brand-100 p-6 rounded-2xl shadow-xl border-2 border-brand-300 hover:shadow-2xl transition-shadow duration-300">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-brand-600 to-brand-700 rounded-xl shadow-lg transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Edit Past GRN</h2>
                <p className="text-gray-700 mt-1 font-medium">
                  Editing an approved GRN{grn?.grnNumber ? ` — ${grn.grnNumber}` : ''} · resubmit for approval
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/inward')}
              className="w-full lg:w-auto justify-center px-6 py-3 min-h-[44px] lg:min-h-0 bg-white text-gray-700 rounded-xl hover:bg-gray-50 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center space-x-2 border-2 border-gray-300"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="whitespace-nowrap">Back to Inbound</span>
            </button>
          </div>
        </div>

        {/* Approval notice */}
        <div className="mb-6 p-4 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3 shadow-sm">
          <i className="fas fa-info-circle text-amber-500 text-lg mt-0.5"></i>
          <p className="text-sm text-amber-800">
            This GRN is already approved. Every change here — quantities, product, batch, pallet
            placement — is staged locally and submitted as one bundle. Nothing touches live inventory
            or storage until an Admin/Super Admin reviews and approves it; if rejected, nothing changes.
          </p>
        </div>

        {/* Reason for edit */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="w-9 h-9 rounded-lg bg-rose-50 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-comment-dots text-rose-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Reason for Edit <span className="text-red-500">*</span></h3>
              <p className="text-xs text-gray-500 mt-0.5">Explain why this GRN needs to change</p>
            </div>
          </div>
          <textarea
            value={editReason}
            onChange={e => setEditReason(e.target.value)}
            rows={2}
            maxLength={2000}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors resize-none"
            placeholder="Why does this GRN need to change?"
          />
        </div>

        {/* Gate Entry Details — read-only, sourced from the gate entry this GRN was created from */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-warehouse text-brand-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Gate Entry Details</h3>
              <p className="text-xs text-gray-500 mt-0.5">Read-only — from the gate entry this GRN was created from</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
            <div className="space-y-1"><p className="text-xs text-gray-500">Entry Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.entryNumber || grn?.gateEntryNumber || '-'}</p></div>
            <div className="space-y-1"><p className="text-xs text-gray-500">Client Name</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.clientName || grn?.clientName || '-'}</p></div>
            <div className="space-y-1"><p className="text-xs text-gray-500">Vehicle Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.vehicleNumber || '-'}</p></div>
            <div className="space-y-1"><p className="text-xs text-gray-500">Expected Qty</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.expectedQuantity || '-'}</p></div>
            <div className="space-y-1"><p className="text-xs text-gray-500">Dock Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.dockNumber || '-'}</p></div>
            <div className="space-y-1"><p className="text-xs text-gray-500">Invoice Number</p>
              <p className="text-sm font-semibold text-gray-900 truncate">{gateEntry?.invoiceNumber || '-'}</p></div>
          </div>
        </div>

        {/* Header fields */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-file-lines text-brand-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">GRN Details</h3>
              <p className="text-xs text-gray-500 mt-0.5">Supplier and logistics details for this GRN</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <div className="sm:col-span-2 lg:col-span-3 space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <i className="fas fa-map-marker-alt text-red-400 text-xs"></i>
                Supplier Name / Address
              </label>
              <textarea value={clientLocation} onChange={e => setClientLocation(e.target.value)} rows={2} maxLength={200}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors resize-none" />
            </div>
            <div className="sm:col-span-2 lg:col-span-3 space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <i className="fas fa-info-circle text-brand-400 text-xs"></i>
                Additional Information
              </label>
              <textarea value={grnRemarks} onChange={e => setGrnRemarks(e.target.value)} rows={2} maxLength={200}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors resize-none" />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <i className="fas fa-car text-gray-400 text-xs"></i>
                Vehicle No
              </label>
              <input value={grn?.vehicleNo || '-'} readOnly
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <i className="fas fa-user text-gray-400 text-xs"></i>
                Driver
              </label>
              <input value={`${grn?.driverName || '-'} ${grn?.driverPhone ? `(${grn.driverPhone})` : ''}`} readOnly
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <i className="fas fa-building text-orange-400 text-xs"></i>
                Transporter Name
              </label>
              <input value={transporterName} onChange={e => setTransporterName(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <i className="fas fa-receipt text-brand-400 text-xs"></i>
                LR Number
              </label>
              <input
                value={lrNumber}
                onChange={e => {
                  setLrNumber(INPUT_SANITIZERS.lrNumberFormat(e.target.value));
                  if (lrNumberError) setLrNumberError(null);
                }}
                onBlur={() => setLrNumberError(FIELD_VALIDATORS.lrNumber(lrNumber))}
                placeholder="e.g. LR2026001234 or LR-2026-001234"
                className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 text-sm transition-colors ${lrNumberError ? 'border-red-400 focus:ring-red-400 focus:border-red-400' : 'border-gray-200 focus:ring-brand-500 focus:border-brand-500'}`}
              />
              {lrNumberError && (
                <p className="text-xs text-red-600 mt-1">{lrNumberError}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
                <i className="fas fa-calendar text-orange-400 text-xs"></i>
                LR Date
              </label>
              <input
                type="date"
                value={lrDate}
                max={todayStr()}
                onChange={e => {
                  if (e.target.value > todayStr()) {
                    toast.error('LR Date cannot be in the future');
                    return;
                  }
                  setLrDate(e.target.value);
                }}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" />
            </div>
          </div>
        </div>

        {/* Edit Item form — shown only while editing an existing line; no new items can be added */}
        <div ref={itemFormRef}>
        {editingKey && (
        <div className="card mb-6 ring-2 ring-brand-200 animate-fade-in">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-pen-to-square text-brand-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Edit Item</h3>
              <p className="text-xs text-gray-500 mt-0.5">Update product, quantities and batch details</p>
            </div>
          </div>

          <div className="relative mb-5">
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
              <i className="fas fa-box text-brand-400 text-xs"></i>
              Product Description <span className="text-red-500">*</span>
            </label>
            {formData.productMasterId ? (
              <div className="flex items-center gap-2 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50">
                <span className="text-sm text-gray-800 flex-1">{formData.productDescription}</span>
                <button type="button" className="text-xs font-semibold text-brand-600 hover:text-brand-800"
                  onClick={() => setFormData(f => ({ ...f, productMasterId: null, productDescription: '', itemCode: '', unitType: '', weightPerItem: '', batchNo: '', skuCode: '', customerSapCode: '', mfgDate: '', expiryDate: '' }))}>
                  Change
                </button>
              </div>
            ) : (
              <input
                value={productSearch}
                onChange={e => { setProductSearch(e.target.value); setShowProductDropdown(true); }}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 150)}
                placeholder="Search product description…"
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors"
              />
            )}
            {showProductDropdown && !formData.productMasterId && filteredProducts.length > 0 && (
              <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredProducts.map((p: any) => (
                  <button key={p.id} type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-brand-50 border-b border-gray-100 last:border-0"
                    onMouseDown={() => selectProduct(p)}>
                    <span className="font-medium">{p.productDescription}</span>
                    {p.itemCode && <span className="ml-2 text-xs text-gray-400">{p.itemCode}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Item Code</label>
              <input value={formData.itemCode} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Unit <span className="text-red-500">*</span></label>
              <input value={formData.unitType} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Weight/Unit (kg)</label>
              <input type="number" value={formData.weightPerItem} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Batch No <span className="text-red-500">*</span></label>
              <select value={formData.batchNo} onChange={e => {
                const selected = batchList.find((b: any) => b.batchNo === e.target.value);
                setFormData(f => ({
                  ...f,
                  batchNo: e.target.value,
                  skuCode: selected?.skuCode ?? f.skuCode,
                  customerSapCode: selected?.customerSapCode ?? f.customerSapCode,
                  mfgDate: selected?.mfgDate ? selected.mfgDate.slice(0, 10) : '',
                  expiryDate: selected?.expiryDate ? selected.expiryDate.slice(0, 10) : '',
                  lotNo: selected?.lotNo || f.lotNo,
                }));
              }} disabled={!formData.productMasterId}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white disabled:bg-gray-100 disabled:cursor-not-allowed">
                <option value="">{!formData.productMasterId ? 'Select product first' : batchList.length === 0 ? 'No batches available' : 'Select batch…'}</option>
                {batchList.map((b: any) => <option key={b.id} value={b.batchNo}>{b.batchNo}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">SKU Code</label>
              <input value={formData.skuCode} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Customer SAP Code</label>
              <input value={formData.customerSapCode} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">MFG Date</label>
              <input type="date" value={formData.mfgDate} disabled={!!formData.mfgDate} onChange={e => setFormData(f => ({ ...f, mfgDate: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Expiry Date</label>
              <input type="date" value={formData.expiryDate} min={formData.mfgDate || undefined} disabled={!!formData.expiryDate} onChange={e => setFormData(f => ({ ...f, expiryDate: e.target.value }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Good Qty <span className="text-red-500">*</span></label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={formData.goodQty} onChange={e => setFormData(f => ({ ...f, goodQty: sanitizeIntegerQty(e.target.value) }))} placeholder="0" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">C.DAM Qty</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={formData.cdamQty} onChange={e => setFormData(f => ({ ...f, cdamQty: sanitizeIntegerQty(e.target.value) }))} placeholder="0" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">DAM Qty</label>
              <input type="text" inputMode="numeric" pattern="[0-9]*" value={formData.damQty} onChange={e => setFormData(f => ({ ...f, damQty: sanitizeIntegerQty(e.target.value) }))} placeholder="0" className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Actual Qty</label>
              <input value={actualQty} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Total Weight (kg)</label>
              <input value={totalWeight.toFixed(2)} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Required Pallets</label>
              <input value={computedRequiredPalletCount ?? 'Auto-calculated'} readOnly className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" /></div>
            <div className="space-y-1.5"><label className="block text-sm font-medium text-gray-700">Lot No</label>
              <input value={formData.lotNo} disabled={!!formData.lotNo} onChange={e => setFormData(f => ({ ...f, lotNo: INPUT_SANITIZERS.lotNoFormat(e.target.value) }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed" /></div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Handling Type <span className="text-red-500">*</span></label>
              <select value={formData.handlingType} onChange={e => setFormData(f => ({ ...f, handlingType: e.target.value as any }))} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors">
                <option value="">— Select —</option>
                <option value="manual">Manual</option>
                <option value="mhe">MHE</option>
              </select>
            </div>
          </div>
          <div className="mt-4 space-y-1.5">
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
              <i className="fas fa-note-sticky text-gray-400 text-xs"></i>
              Remarks
            </label>
            <textarea value={formData.remarks} onChange={e => setFormData(f => ({ ...f, remarks: e.target.value }))} rows={2} maxLength={200}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors resize-none" />
          </div>

          <div className="flex justify-end gap-3 mt-5">
            <Button variant="outline" onClick={resetForm}>Cancel</Button>
            <Button variant="primary" icon="fa-check" onClick={handleUpdateItem}>Update Item</Button>
          </div>
        </div>
        )}
        </div>

        {/* Items table */}
        <div className="card mb-6">
          <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
            <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center flex-shrink-0">
              <i className="fas fa-boxes-stacked text-brand-600 text-base"></i>
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">Products ({stagedItems.length})</h3>
              <p className="text-xs text-gray-500 mt-0.5">Edit details or reassign pallets for each line</p>
            </div>
          </div>

          {stagedItems.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-500 text-sm">No items on this GRN.</p>
            </div>
          ) : (
            <>
              <MobileCardList columns={itemColumns} data={stagedItems} />
              <div className="hidden lg:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Batch</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Good</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">C.DAM</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">DAM</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pallets</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Storage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stagedItems.map(item => (
                      <tr key={item._key} className={`hover:bg-gray-50 transition-colors ${assigningKey === item._key ? 'bg-brand-50/60' : ''}`}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.productDescription}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{item.batchNo || '-'}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.goodQty}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.cdamQty}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.damQty}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900">{item.palletCount || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatStorageLocations(item.storageDetails) || <span className="text-amber-600 font-medium">Not assigned</span>}</td>
                        <td className="px-4 py-3 text-sm">
                          {item._action === 'existing'
                            ? <span className="text-gray-400">—</span>
                            : <Badge variant={item._action === 'added' ? 'success' : 'warning'} size="sm" rounded>{item._action === 'added' ? 'New' : 'Edited'}</Badge>}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                            <button onClick={() => handleEditItem(item)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 transition-colors">
                              <i className="fas fa-pen text-[10px]"></i> Edit
                            </button>
                            <button onClick={() => (assigningKey === item._key ? resetCardAssignment() : openPalletCard(item))} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-brand-700 bg-brand-50 hover:bg-brand-100 transition-colors">
                              <i className="fas fa-pallet text-[10px]"></i> {assigningKey === item._key ? 'Close' : 'Pallet'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Pallet assignment panel */}
          {assigningKey && (() => {
            const item = stagedItems.find(it => it._key === assigningKey);
            if (!item) return null;
            const damagedQty = item.cdamQty + item.damQty;
            const zonesForPicker = cardIsOversized ? heavyZones : nonLbdZones;
            return (
              <div className="mt-5 rounded-xl border border-brand-200 bg-gradient-to-br from-brand-50 to-brand-50 p-5 space-y-4 animate-fade-in">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-pallet text-brand-600 text-sm"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-gray-900">Assign Pallets — {item.productDescription}</h4>
                </div>

                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">Required Pallets</label>
                    <input value={requiredPalletsForItem(item)} readOnly
                      className="w-32 px-3 py-2.5 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 text-sm cursor-not-allowed" />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-gray-700">Storage Type</label>
                    <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
                      {(['ground', 'rack'] as const).map(t => {
                        const disabled = t === 'rack' && racksSorted.length === 0;
                        return (
                          <button key={t} type="button" disabled={disabled} onClick={() => setCardStorageType(t)}
                            title={disabled ? 'No racks configured for this warehouse' : undefined}
                            className={`px-4 py-2 rounded-md text-sm font-semibold capitalize transition-colors ${cardStorageType === t ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-gray-50'} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}>
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {cardStorageType === 'ground' && (
                    <label className="flex items-center gap-2 text-sm font-medium text-gray-700 h-[42px]">
                      <input type="checkbox" checked={cardIsOversized} onChange={e => setCardIsOversized(e.target.checked)} className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
                      Oversized (Heavy zone only)
                    </label>
                  )}
                  {cardStorageType === 'ground' && cardIsOversized && (
                    <div className="space-y-1.5">
                      <label className="block text-sm font-medium text-gray-700">Pallet Size (sqft)</label>
                      <input type="text" inputMode="decimal" pattern="[0-9]*\.?[0-9]*" value={cardPalletSizeSqft} onChange={e => setCardPalletSizeSqft(sanitizeDecimalQty(e.target.value))}
                        className="w-32 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white" />
                    </div>
                  )}
                </div>

                {cardStorageType === 'ground' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Ground Zone Placement</label>
                  {cardGroundPlacements.map((row, idx) => {
                    const locs = row.zoneId !== '' ? (zoneLocationsByZoneId[row.zoneId as number] || []) : [];
                    const takenGroundLocations = new Set(cardGroundPlacements.filter((_, i) => i !== idx).map(r => r.zoneLocationId).filter(Boolean));
                    // FCFS: every pallet except a genuinely partial last one must go to a
                    // completely empty slot. Only when the last pallet doesn't need a full
                    // pallet's worth of space do we relax to "enough free % remains".
                    const isLastPallet = idx === cardGroundPlacements.length - 1;
                    const remainder = item.qtyPerPallet && item.qtyPerPallet > 0 ? item.goodQty % item.qtyPerPallet : 0;
                    const isPartialLastPallet = isLastPallet && !!item.qtyPerPallet && item.qtyPerPallet > 0 && remainder > 0;
                    const requiredFreePct = isPartialLastPallet ? (remainder / (item.qtyPerPallet as number)) * 100 : 100;
                    const CAPACITY_TOLERANCE_PCT = 0.5;
                    return (
                      <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <select value={row.zoneId} onChange={e => {
                          const zoneId = Number(e.target.value);
                          setCardGroundPlacements(prev => prev.map((r, i) => i === idx ? { zoneId, pallets: r.pallets, zoneLocationId: null } : r));
                          if (zoneId) void ensureZoneLocationsLoaded(zoneId);
                        }} className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white">
                          <option value="">— Zone —</option>
                          {zonesForPicker.map((z: any) => <option key={z.id} value={z.id}>{zoneOptionLabel(z)}</option>)}
                        </select>
                        <div className="flex-1">
                        <select value={row.zoneLocationId ?? ''} onChange={e => {
                          const zoneLocationId = e.target.value ? Number(e.target.value) : null;
                          setCardGroundPlacements(prev => prev.map((r, i) => i === idx ? { ...r, zoneLocationId, pallets: zoneLocationId ? '1' : r.pallets } : r));
                        }} disabled={row.zoneId === ''} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white disabled:bg-gray-50">
                          <option value="">— Location —</option>
                          {locs.map((loc: any) => {
                            const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
                            return (
                              <option key={loc.locationId} value={loc.locationId} disabled={loc.status === 'occupied' || loc.isHeld || (loc.locationId !== row.zoneLocationId && (takenGroundLocations.has(loc.locationId) || freePct < requiredFreePct - CAPACITY_TOLERANCE_PCT))}>
                                {loc.locationCode}{formatLocationSpaceLabel(loc)}
                              </option>
                            );
                          })}
                        </select>
                        {isPartialLastPallet && (
                          <p className="mt-1 text-[11px] text-brand-500">Partial last pallet — slots with ≥{Math.round(requiredFreePct)}% free are allowed</p>
                        )}
                        </div>
                        <div className="flex items-center gap-2">
                          <input type="text" inputMode="numeric" pattern="[0-9]*" value={row.pallets} disabled={!!row.zoneLocationId} onChange={e => setCardGroundPlacements(prev => prev.map((r, i) => i === idx ? { ...r, pallets: sanitizeIntegerQty(e.target.value) } : r))}
                            placeholder="Pallets" className="w-full sm:w-24 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500" />
                        </div>
                      </div>
                    );
                  })}
                  {(() => {
                    // Rows are system-generated from Required Pallets (one pallet each) —
                    // the user only assigns a location per row. Show placement progress.
                    const required = requiredPalletsForItem(item);
                    const placed = cardGroundPlacements.filter(p => p.zoneLocationId).length;
                    if (required <= 0) return null;
                    return (
                      <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${placed === required ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {placed} / {required} pallets placed
                      </span>
                    );
                  })()}
                </div>
                )}

                {cardStorageType === 'rack' && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Rack Placement</label>
                  {cardRackPlacements.map((row, idx) => {
                    const locs = row.rackId ? (locationsByRackId[row.rackId] || []) : [];
                    const takenRackLocations = new Set(cardRackPlacements.filter((_, i) => i !== idx).map(r => r.locationId).filter(Boolean));
                    // FCFS: every pallet except a genuinely partial last one must go to a
                    // completely empty location. Only when the last pallet doesn't need a
                    // full pallet's worth of space do we relax to "enough free % remains".
                    const isLastPallet = idx === cardRackPlacements.length - 1;
                    const remainder = item.qtyPerPallet && item.qtyPerPallet > 0 ? item.goodQty % item.qtyPerPallet : 0;
                    const isPartialLastPallet = isLastPallet && !!item.qtyPerPallet && item.qtyPerPallet > 0 && remainder > 0;
                    const requiredFreePct = isPartialLastPallet ? (remainder / (item.qtyPerPallet as number)) * 100 : 100;
                    const CAPACITY_TOLERANCE_PCT = 0.5;
                    return (
                      <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <select value={row.rackId ?? ''} onChange={e => {
                          const rackId = e.target.value ? Number(e.target.value) : null;
                          setCardRackPlacements(prev => prev.map((r, i) => i === idx ? { rackId, locationId: null } : r));
                          if (rackId) void ensureRackLocationsLoaded(rackId);
                        }} className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white">
                          <option value="">— Rack —</option>
                          {racksSorted.map((r: any) => <option key={r.id} value={r.id}>{rackOptionLabel(r)}</option>)}
                        </select>
                        <div className="flex-1">
                        <select value={row.locationId ?? ''} onChange={e => {
                          const locationId = e.target.value ? Number(e.target.value) : null;
                          setCardRackPlacements(prev => prev.map((r, i) => i === idx ? { ...r, locationId } : r));
                        }} disabled={!row.rackId} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white disabled:bg-gray-50">
                          <option value="">— Location —</option>
                          {locs.map((loc: any) => {
                            const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
                            return (
                              <option key={loc.locationId} value={loc.locationId} disabled={loc.status === 'occupied' || loc.isHeld || (loc.locationId !== row.locationId && (takenRackLocations.has(loc.locationId) || freePct < requiredFreePct - CAPACITY_TOLERANCE_PCT))}>
                                {loc.locationCode}{formatLocationSpaceLabel(loc)}
                              </option>
                            );
                          })}
                        </select>
                        {isPartialLastPallet && (
                          <p className="mt-1 text-[11px] text-brand-500">Partial last pallet — locations with ≥{Math.round(requiredFreePct)}% free are allowed</p>
                        )}
                        </div>
                      </div>
                    );
                  })}
                  {(() => {
                    const required = requiredPalletsForItem(item);
                    const placed = cardRackPlacements.filter(p => p.locationId).length;
                    if (required <= 0) return null;
                    return (
                      <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${placed === required ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                        {placed} / {required} pallets placed
                      </span>
                    );
                  })()}
                </div>
                )}

                {damagedQty > 0 && (
                  <div className="space-y-2 border-t border-brand-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      LBD Placement <span className="font-normal text-gray-500">(required — {damagedQty} damaged/cosmetic-damaged units)</span>
                    </label>
                    <div className="flex gap-2 w-48">
                      <button type="button" onClick={() => {
                        setCardLbdPlacementType('zone');
                        setCardLbdPlacements(prev => prev.map(() => ({ zoneId: '' as number | '', pallets: '1', zoneLocationId: null, rackId: null, locationId: null })));
                      }} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${cardLbdPlacementType === 'zone' ? 'bg-brand-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}>Zone</button>
                      <button type="button" onClick={() => {
                        setCardLbdPlacementType('rack');
                        setCardLbdPlacements(prev => prev.map(() => ({ zoneId: '' as number | '', pallets: '1', zoneLocationId: null, rackId: null, locationId: null })));
                      }} className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${cardLbdPlacementType === 'rack' ? 'bg-brand-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'}`}>Rack</button>
                    </div>
                    {cardLbdPlacementType === 'rack' ? cardLbdPlacements.map((row, idx) => {
                          const locs = row.rackId ? (locationsByRackId[row.rackId] || []) : [];
                          const takenLbdLocations = new Set(cardLbdPlacements.filter((_, i) => i !== idx).map(r => r.locationId).filter(Boolean));
                          // Same FCFS rule as the good-stock Rack Placement above, sized
                          // against the damaged/cosmetic-damaged quantity instead of goodQty.
                          const isLastPallet = idx === cardLbdPlacements.length - 1;
                          const remainder = item.qtyPerPallet && item.qtyPerPallet > 0 ? damagedQty % item.qtyPerPallet : 0;
                          const isPartialLastPallet = isLastPallet && !!item.qtyPerPallet && item.qtyPerPallet > 0 && remainder > 0;
                          const requiredFreePct = isPartialLastPallet ? (remainder / (item.qtyPerPallet as number)) * 100 : 100;
                          const CAPACITY_TOLERANCE_PCT = 0.5;
                          return (
                            <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <select value={row.rackId ?? ''} onChange={e => {
                                const rackId = e.target.value ? Number(e.target.value) : null;
                                setCardLbdPlacements(prev => prev.map((r, i) => i === idx ? { ...r, rackId, locationId: null } : r));
                                if (rackId) void ensureRackLocationsLoaded(rackId);
                              }} className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white">
                                <option value="">— LBD Rack —</option>
                                {lbdRacksSorted.map((r: any) => <option key={r.id} value={r.id}>{rackOptionLabel(r)}</option>)}
                              </select>
                              <div className="flex-1">
                              <select value={row.locationId ?? ''} onChange={e => {
                                const locationId = e.target.value ? Number(e.target.value) : null;
                                setCardLbdPlacements(prev => prev.map((r, i) => i === idx ? { ...r, locationId } : r));
                              }} disabled={!row.rackId} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white disabled:bg-gray-50">
                                <option value="">— Location —</option>
                                {locs.map((loc: any) => {
                                  const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
                                  return (
                                    <option key={loc.locationId} value={loc.locationId} disabled={loc.status === 'occupied' || loc.isHeld || (loc.locationId !== row.locationId && (takenLbdLocations.has(loc.locationId) || freePct < requiredFreePct - CAPACITY_TOLERANCE_PCT))}>
                                      {loc.locationCode}{formatLocationSpaceLabel(loc)}
                                    </option>
                                  );
                                })}
                              </select>
                              {isPartialLastPallet && (
                                <p className="mt-1 text-[11px] text-brand-500">Partial last pallet — locations with ≥{Math.round(requiredFreePct)}% free are allowed</p>
                              )}
                              </div>
                            </div>
                          );
                        }) : cardLbdPlacements.map((row, idx) => {
                          const locs = row.zoneId !== '' ? (zoneLocationsByZoneId[row.zoneId as number] || []) : [];
                          const takenLbdLocations = new Set(cardLbdPlacements.filter((_, i) => i !== idx).map(r => r.zoneLocationId).filter(Boolean));
                          // Same FCFS rule as the good-stock Ground Zone Placement above, sized
                          // against the damaged/cosmetic-damaged quantity instead of goodQty.
                          const isLastPallet = idx === cardLbdPlacements.length - 1;
                          const remainder = item.qtyPerPallet && item.qtyPerPallet > 0 ? damagedQty % item.qtyPerPallet : 0;
                          const isPartialLastPallet = isLastPallet && !!item.qtyPerPallet && item.qtyPerPallet > 0 && remainder > 0;
                          const requiredFreePct = isPartialLastPallet ? (remainder / (item.qtyPerPallet as number)) * 100 : 100;
                          const CAPACITY_TOLERANCE_PCT = 0.5;
                          return (
                            <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-2">
                              <select value={row.zoneId} onChange={e => {
                                const zoneId = Number(e.target.value);
                                setCardLbdPlacements(prev => prev.map((r, i) => i === idx ? { zoneId, pallets: r.pallets, zoneLocationId: null } : r));
                                if (zoneId) void ensureZoneLocationsLoaded(zoneId);
                              }} className="flex-1 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white">
                                <option value="">— LBD Zone —</option>
                                {lbdZones.map((z: any) => <option key={z.id} value={z.id}>{zoneOptionLabel(z)}</option>)}
                              </select>
                              <div className="flex-1">
                              <select value={row.zoneLocationId ?? ''} onChange={e => {
                                const zoneLocationId = e.target.value ? Number(e.target.value) : null;
                                setCardLbdPlacements(prev => prev.map((r, i) => i === idx ? { ...r, zoneLocationId, pallets: zoneLocationId ? '1' : r.pallets } : r));
                              }} disabled={row.zoneId === ''} className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white disabled:bg-gray-50">
                                <option value="">— Location —</option>
                                {locs.map((loc: any) => {
                                  const freePct = 100 - Number(loc.occupiedPercentage ?? 0);
                                  return (
                                    <option key={loc.locationId} value={loc.locationId} disabled={loc.status === 'occupied' || loc.isHeld || (loc.locationId !== row.zoneLocationId && (takenLbdLocations.has(loc.locationId) || freePct < requiredFreePct - CAPACITY_TOLERANCE_PCT))}>
                                      {loc.locationCode}{formatLocationSpaceLabel(loc)}
                                    </option>
                                  );
                                })}
                              </select>
                              {isPartialLastPallet && (
                                <p className="mt-1 text-[11px] text-brand-500">Partial last pallet — slots with ≥{Math.round(requiredFreePct)}% free are allowed</p>
                              )}
                              </div>
                              <div className="flex items-center gap-2">
                                <input type="text" inputMode="numeric" pattern="[0-9]*" value={row.pallets} disabled={!!row.zoneLocationId} onChange={e => setCardLbdPlacements(prev => prev.map((r, i) => i === idx ? { ...r, pallets: sanitizeIntegerQty(e.target.value) } : r))}
                                  placeholder="Pallets" className="w-full sm:w-24 px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500" />
                              </div>
                            </div>
                          );
                        })}
                        {(() => {
                          // LBD rows are system-generated (one pallet per location) — the
                          // user only assigns a location per row. Show placement progress.
                          const lbdRequired = cardLbdPlacements.length;
                          const lbdPlaced = cardLbdPlacementType === 'rack'
                            ? cardLbdPlacements.filter(p => p.locationId).length
                            : cardLbdPlacements.filter(p => p.zoneLocationId).length;
                          if (lbdRequired <= 0) return null;
                          return (
                            <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${lbdPlaced === lbdRequired ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {lbdPlaced} / {lbdRequired} LBD pallets placed
                            </span>
                          );
                        })()}
                    <input value={cardLbdRemark} onChange={e => setCardLbdRemark(e.target.value)} placeholder="LBD remark (optional)"
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500 text-sm transition-colors bg-white" />
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-1">
                  <Button variant="outline" onClick={resetCardAssignment}>Cancel</Button>
                  <Button variant="primary" icon="fa-check" onClick={() => handleSavePalletAssignment(item)} className="!bg-brand-600 hover:!bg-brand-700 focus:!ring-brand-500">Save Assignment</Button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Footer actions */}
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate('/inward')}>Cancel</Button>
          <Button variant="success" icon="fa-paper-plane" disabled={isSubmitting} onClick={handleSubmit}>
            {isSubmitting ? 'Submitting…' : 'Submit for Approval'}
          </Button>
        </div>
      </div>
      <ConfirmDialog />
    </Layout>
  );
}
